import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { getProfileProgress } from 'wasp/client/operations';
import { useUser } from '../hooks/useUser';
import React from 'react';

type Progress = { 
  overallPct: number;
  requiredDone: number; 
  requiredTotal: number;
};

interface ProfileProgressState {
  progress: Progress | null;
  isLoading: boolean;
  set: (p: Progress) => void;
  refresh: (isAuthenticated?: boolean) => Promise<void>;
}

const createProfileProgressState = (set: any, get: any): ProfileProgressState => ({
  progress: null,
  isLoading: false,
  
  set: (progress: Progress) => set({ progress }),
  
  refresh: async (isAuthenticated?: boolean) => {
    // Gate: Don't make API calls if not authenticated
    if (isAuthenticated === false) {
      console.debug('ProfileProgressStore: Skipping refresh - not authenticated');
      set({ progress: null, isLoading: false });
      return;
    }
    
    set({ isLoading: true });
    try {
      try {
        const progress = await getProfileProgress({}) as any as Progress;
        set({ progress, isLoading: false });
      } catch (error: any) {
        // Don't spam console with 401s when logged out
        if (error?.status === 401 || error?.statusCode === 401) {
          console.debug('ProfileProgressStore: 401 - user not authenticated');
          set({ progress: null, isLoading: false });
        } else {
          console.warn('Failed to fetch profile progress');
          console.error(error);
          set({ isLoading: false });
        }
      }
    } catch (error) {
      console.error('Error fetching profile progress:', error);
      set({ isLoading: false });
    }
  }
});

// Export the hook that accepts selectors
export const useProfileProgress: UseBoundStore<StoreApi<ProfileProgressState>> = 
  create<ProfileProgressState>()(createProfileProgressState);

// Typed selectors
export const selectProgress = (s: ProfileProgressState) => s.progress;
export const selectProgressLoading = (s: ProfileProgressState) => s.isLoading;
export const selectOverallPct = (s: ProfileProgressState) => s.progress?.overallPct ?? 0;

// Helper hook for optimistic updates
export const useOptimisticProfileUpdate = () => {
  const { set, refresh } = useProfileProgress();
  
  return async (
    nextProgress: Progress,
    apiCall: () => Promise<void>
  ) => {
    // Optimistic update
    set(nextProgress);
    
    try {
      // Make the API call
      await apiCall();
      // Refresh to get authoritative data
      await refresh();
    } catch (error) {
      // On error, refresh to get correct state
      console.error('Profile update failed:', error);
      await refresh();
      throw error;
    }
  };
};

// Hook to connect auth state with store
export const useAuthenticatedProfileProgress: UseBoundStore<StoreApi<ProfileProgressState>> = useProfileProgress;

// Hook that auto-refreshes based on auth state  
export const useAuthenticatedProfileProgressWithRefresh = () => {
  const { isAuthenticated, isLoading: authLoading } = useUser();
  const store = useProfileProgress();
  
  // Auto-refresh when auth state changes
  React.useEffect(() => {
    if (!authLoading) {
      store.refresh(isAuthenticated);
    }
  }, [isAuthenticated, authLoading]);
  
  return store;
};

// Auto-refresh on page focus (only if authenticated)
if (typeof window !== 'undefined') {
  let isAuth = false;
  
  const handleVisibilityChange = () => {
    if (!document.hidden && isAuth) {
      useProfileProgress.getState().refresh(true);
    }
  };
  
  // Track auth state for visibility handler
  const updateAuthState = () => {
    // This will be updated by the hook above
    const state = useProfileProgress.getState();
    isAuth = state.progress !== null; // Simple heuristic
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
  setInterval(updateAuthState, 1000); // Check auth state periodically
}
